import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useNumber, setValue, increment, decrement, resetValue, flush } from "../../hooks/use-number.es.js";
import { Pill } from "../pill/pill.es.js";
import { TensorGridInputWrapper, TensorGridInputImpl } from "./tensor-grid-input.styles.es.js";
const propTypes = {
  align: PropTypes.oneOf(["left", "right"]),
  column: PropTypes.string.isRequired,
  dataType: PropTypes.oneOf(["number", "text"]),
  editMode: PropTypes.bool,
  firstColumn: PropTypes.bool,
  format: PropTypes.func,
  itemKey: PropTypes.string.isRequired,
  lastColumn: PropTypes.bool,
  lastRow: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  onEditModeChange: PropTypes.func,
  step: PropTypes.number,
  units: PropTypes.string,
  value: PropTypes.string
};
const defaultProps = {
  align: "left",
  dataType: "text",
  editMode: false,
  firstColumn: false,
  format: void 0,
  lastColumn: false,
  lastRow: false,
  max: void 0,
  min: void 0,
  onChange: void 0,
  onEditModeChange: void 0,
  step: 1,
  units: void 0,
  value: void 0
};
const TensorGridInput = ({
  align,
  column,
  dataType,
  editMode,
  firstColumn,
  format,
  itemKey,
  lastColumn,
  lastRow,
  max,
  min,
  onEditModeChange,
  onChange,
  step,
  units,
  value: initialValue
}) => {
  const inputRef = useRef();
  const [{ dirty, value }, dispatch] = useNumber({
    max,
    min,
    step,
    value: initialValue
  });
  const [hasFocus, setFocus] = useState(false);
  const inputMode = hasFocus && editMode;
  const handleClick = useCallback(() => {
    const { length } = inputRef.current.value;
    onEditModeChange(true);
    inputRef.current.setSelectionRange(length, length);
    inputRef.current.focus();
  }, [onEditModeChange]);
  const handleFocus = useCallback(() => {
    setFocus(true);
  }, []);
  const handleBlur = useCallback(() => {
    setFocus(false);
  }, []);
  const handleInput = useCallback(
    (event) => {
      dispatch(setValue(event.currentTarget.value));
    },
    [dispatch]
  );
  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case "Enter":
          event.preventDefault();
          onEditModeChange(!editMode);
          break;
        case "Escape":
          event.preventDefault();
          dispatch(setValue(initialValue));
          onEditModeChange(false);
          break;
        case "ArrowDown":
          event.preventDefault();
          if (dataType === "number") {
            dispatch(decrement());
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (dataType === "number") {
            dispatch(increment());
          }
          break;
      }
    },
    [dataType, dispatch, editMode, initialValue, onEditModeChange]
  );
  useEffect(() => {
    if (!inputMode) {
      if (dirty) {
        if (dataType === "number") {
          if (value.trim() === "" || Number.isNaN(Number(value))) {
            dispatch(resetValue(initialValue));
          } else {
            onChange(itemKey, column, Number(value));
            dispatch(flush());
          }
        } else {
          onChange(itemKey, column, value);
          dispatch(flush());
        }
      } else if (value !== String(initialValue)) {
        dispatch(resetValue(initialValue));
      }
    }
  }, [
    column,
    dataType,
    dirty,
    dispatch,
    initialValue,
    inputMode,
    itemKey,
    onChange,
    value
  ]);
  return /* @__PURE__ */ jsxs(
    TensorGridInputWrapper,
    {
      role: "button",
      onClick: handleClick,
      $firstColumn: firstColumn,
      $inputMode: inputMode,
      $lastColumn: lastColumn,
      $lastRow: lastRow,
      tabIndex: -1,
      children: [
        /* @__PURE__ */ jsx(
          TensorGridInputImpl,
          {
            ref: inputRef,
            onBlur: handleBlur,
            onChange: handleInput,
            onFocus: handleFocus,
            onKeyDown: handleKeyDown,
            readOnly: !inputMode,
            size: "1",
            value: !inputMode && format ? format(dataType === "number" ? Number(value) : value) : value,
            $align: align
          }
        ),
        units && /* @__PURE__ */ jsx(Pill, { children: units })
      ]
    }
  );
};
TensorGridInput.propTypes = propTypes;
TensorGridInput.defaultProps = defaultProps;
export {
  TensorGridInput
};
